// AdminBookingsTab — admin/super-admin booking management.
//
// Lists every booking across users with filters (status, search by event/user)
// and exposes a Refund action that opens a modal to collect optional partial
// amount + reason, then calls POST /api/bookings/{id}/refund.
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import AppModal from '../ui/AppModal';

const STATUS_COLORS = {
  paid:      'bg-green-100 text-green-700',
  confirmed: 'bg-blue-100 text-blue-700',
  refunded:  'bg-gray-100 text-gray-500',
  cancelled: 'bg-red-100 text-red-700',
};

function fmtINR(n) {
  if (!n && n !== 0) return '—';
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

function fmtDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function AdminBookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [refundTarget, setRefundTarget] = useState(null); // booking row to refund

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const r = await axiosInstance.get('/admin/bookings', { params });
      setBookings(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterStatus]);

  const filtered = bookings.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (b.event_title || '').toLowerCase().includes(q) ||
      (b.user_name  || '').toLowerCase().includes(q) ||
      (b.user_email || '').toLowerCase().includes(q) ||
      (b.id || '').toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-bookings-tab" className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
        <input
          data-testid="admin-bookings-search"
          type="text"
          placeholder="Search event, user, email, booking ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#2B3A67]"
        />
        <div className="flex gap-2 overflow-x-auto">
          {[
            { v: '',          label: 'All' },
            { v: 'paid',      label: 'Paid' },
            { v: 'confirmed', label: 'Confirmed' },
            { v: 'refunded',  label: 'Refunded' },
            { v: 'cancelled', label: 'Cancelled' },
          ].map((opt) => (
            <button
              key={opt.v || 'all'}
              data-testid={`admin-bookings-filter-${opt.v || 'all'}`}
              onClick={() => setFilterStatus(opt.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${
                filterStatus === opt.v
                  ? 'bg-[#2B3A67] text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-gray-400">{filtered.length} bookings</p>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div data-testid="admin-bookings-empty" className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <i className="ph-light ph-ticket text-5xl text-gray-300 mb-2 block" />
          <p className="text-sm text-gray-500">No bookings match your filters</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((b) => (
            <div
              key={b.id}
              data-testid={`admin-booking-row-${b.id}`}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex justify-between items-start gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-[#2B3A67] truncate">{b.event_title || '—'}</p>
                  <p className="text-xs text-gray-500 truncate">{b.user_name} · {b.user_email}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>
                  {b.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div>
                  <span className="text-gray-400">Amount</span>
                  <p className="font-bold">{fmtINR(b.total_amount)}</p>
                </div>
                <div>
                  <span className="text-gray-400">Booked</span>
                  <p>{fmtDate(b.created_at)}</p>
                </div>
                {b.razorpay_payment_id && (
                  <div className="col-span-2">
                    <span className="text-gray-400">Razorpay payment</span>
                    <p className="font-mono text-[11px] truncate">{b.razorpay_payment_id}</p>
                  </div>
                )}
                {b.refund_amount && (
                  <div className="col-span-2 text-amber-700 bg-amber-50 rounded-lg p-2">
                    <span className="text-[10px] font-bold uppercase">Refunded</span>
                    <p>{fmtINR(b.refund_amount)} on {fmtDate(b.refunded_at)}</p>
                    {b.refund_reason && <p className="italic mt-0.5">"{b.refund_reason}"</p>}
                    {b.razorpay_refund_id && <p className="font-mono text-[10px] mt-0.5">{b.razorpay_refund_id}</p>}
                  </div>
                )}
              </div>

              {b.status !== 'refunded' && b.status !== 'cancelled' && (
                <button
                  data-testid={`admin-refund-btn-${b.id}`}
                  onClick={() => setRefundTarget(b)}
                  className="w-full px-3 py-2 rounded-xl bg-[#E54562] text-white text-xs font-bold hover:bg-[#d13a55]"
                >
                  Refund
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <RefundModal
        booking={refundTarget}
        onClose={() => setRefundTarget(null)}
        onSuccess={() => { setRefundTarget(null); load(); }}
      />
    </div>
  );
}


function RefundModal({ booking, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset form on each open
  useEffect(() => {
    if (booking) {
      setAmount('');
      setReason('');
    }
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!booking) return;
    const body = {};
    if (amount) {
      const n = parseFloat(amount);
      if (Number.isNaN(n) || n <= 0) {
        toast.error('Enter a valid refund amount or leave blank for full refund');
        return;
      }
      if (n > (booking.total_amount || 0)) {
        toast.error(`Cannot exceed booking total ${fmtINR(booking.total_amount)}`);
        return;
      }
      body.amount = n;
    }
    if (reason.trim()) body.reason = reason.trim();

    setSubmitting(true);
    try {
      const r = await axiosInstance.post(`/bookings/${booking.id}/refund`, body);
      toast.success(
        r.data?.razorpay_refund_id
          ? `Refund initiated · ${r.data.razorpay_refund_id}`
          : 'Refund processed'
      );
      onSuccess();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Refund failed';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={!!booking}
      onClose={onClose}
      title="Process refund"
      subtitle={booking?.event_title}
      size="md"
      testId="refund-modal"
    >
      {booking && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-3 text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">User</span>
              <span className="font-semibold truncate">{booking.user_name} · {booking.user_email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Booking total</span>
              <span className="font-bold">{fmtINR(booking.total_amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Payment</span>
              <span className="font-mono text-[11px] truncate ml-2">{booking.razorpay_payment_id || 'Free / no payment ID'}</span>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Refund amount (₹)
            </label>
            <input
              data-testid="refund-modal-amount"
              type="number"
              step="0.01"
              min="0"
              max={booking.total_amount || undefined}
              placeholder={`Leave blank for full refund (${fmtINR(booking.total_amount)})`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Optional. Leave blank to refund the full booking amount.
            </p>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Reason (optional)
            </label>
            <textarea
              data-testid="refund-modal-reason"
              rows={3}
              placeholder="Will be visible to the user in their notification & email"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
            />
          </div>

          {booking.razorpay_payment_id ? (
            <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg p-2.5 leading-relaxed">
              <i className="ph-bold ph-warning mr-1" />
              This will issue a real Razorpay refund. Funds will reach the user
              in 5–7 business days. The action cannot be undone from this UI.
            </p>
          ) : (
            <p className="text-[11px] text-gray-600 bg-gray-50 rounded-lg p-2.5 leading-relaxed">
              No Razorpay payment on this booking — only the booking status will
              be updated and the user will be notified.
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              data-testid="refund-modal-cancel"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              data-testid="refund-modal-confirm"
              onClick={submit}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl bg-[#E54562] text-white font-bold text-sm hover:bg-[#d13a55] disabled:opacity-60"
            >
              {submitting ? 'Processing…' : 'Confirm Refund'}
            </button>
          </div>
        </div>
      )}
    </AppModal>
  );
}
